import type { Action, FightResult, FightState, FighterState } from './types';

function cloneAction(a: Action | null): Action | null {
  return a ? { type: a.type, t: a.t, dur: a.dur, done: a.done } : null;
}

function cloneFighter(f: FighterState): FighterState {
  return { ...f, act: cloneAction(f.act), damageDealt: [...f.damageDealt] };
}

function cloneResult(r: FightResult): FightResult {
  const out: FightResult = { ...r, roundWinners: [...r.roundWinners], damage: [...r.damage] };
  if (r.skillDamage) out.skillDamage = [{ ...r.skillDamage[0] }, { ...r.skillDamage[1] }];
  return out;
}

/** Deep copy for rollback and replays. The setup is immutable and shared. */
export function cloneFight(s: FightState): FightState {
  return {
    ...s,
    fighters: [cloneFighter(s.fighters[0]), cloneFighter(s.fighters[1])],
    roundWinners: [...s.roundWinners],
    result: s.result ? cloneResult(s.result) : null,
  };
}

function copyFighterInto(dst: FighterState, src: FighterState): void {
  const act = dst.act;
  const dmg = dst.damageDealt;
  Object.assign(dst, src);
  if (src.act) {
    const a = act ?? { type: src.act.type, t: 0, dur: 0, done: false };
    a.type = src.act.type;
    a.t = src.act.t;
    a.dur = src.act.dur;
    a.done = src.act.done;
    dst.act = a;
  } else {
    dst.act = null;
  }
  dmg.length = src.damageDealt.length;
  for (let i = 0; i < dmg.length; i++) dmg[i] = src.damageDealt[i]!;
  dst.damageDealt = dmg;
}

/**
 * Copies `src` into `dst` reusing dst's objects, so a renderer can keep a "previous state"
 * every tick without allocating (no garbage-collection stalls).
 */
export function copyFightInto(dst: FightState, src: FightState): void {
  const fighters = dst.fighters;
  const winners = dst.roundWinners;
  copyFighterInto(fighters[0], src.fighters[0]);
  copyFighterInto(fighters[1], src.fighters[1]);
  Object.assign(dst, src);
  dst.fighters = fighters;
  winners.length = src.roundWinners.length;
  for (let i = 0; i < winners.length; i++) winners[i] = src.roundWinners[i]!;
  dst.roundWinners = winners;
  dst.result = src.result;
}

const PHASES = ['intro', 'fight', 'timeUp', 'break', 'ko', 'decision', 'over'] as const;
const ACTIONS = ['strike', 'strike2', 'grapple', 'signature', 'grab', 'grabbed', 'stun', 'down'] as const;

/**
 * FNV-1a over the exact bits of every number that matters. Two states with equal hashes
 * are (practically) identical, which is how desyncs and cross-engine drift are detected.
 */
export function hashFight(s: FightState): number {
  const buf = new Float64Array(1);
  const bytes = new Uint8Array(buf.buffer);
  let h = 0x811c9dc5;
  const num = (v: number) => {
    buf[0] = v;
    for (let i = 0; i < 8; i++) {
      h ^= bytes[i]!;
      h = Math.imul(h, 0x01000193);
    }
  };
  num(s.tick);
  num(PHASES.indexOf(s.phase));
  num(s.phaseT);
  num(s.round);
  num(s.clock);
  num(s.timeScale);
  num(s.rng);
  for (const f of s.fighters) {
    num(f.x);
    num(f.y);
    num(f.vx);
    num(f.vy);
    num(f.kx);
    num(f.ky);
    num(f.face);
    num(f.hp);
    num(f.stamina);
    num(f.meter);
    num(f.act ? ACTIONS.indexOf(f.act.type) : -1);
    num(f.act ? f.act.t : -1);
    num(f.act?.done ? 1 : 0);
    num(f.actSeq);
    num(f.queued ? f.queued.length : 0);
    num(f.queuedT);
    num(f.blocking ? 1 : 0);
    num(f.blockHeld);
    num(f.prevButtons);
    num(f.ko ? 1 : 0);
    num(f.grappleImmune);
    num(f.oneTwoWindow);
    num(f.ironChinUsed ? 1 : 0);
    num(f.rocked);
    num(f.rockedThisRound ? 1 : 0);
    num(f.recovered);
    num(f.punishWindow);
    num(f.grabRead ? 1 : 0);
    for (const d of f.damageDealt) num(d);
    num(f.powerDamage);
    num(f.takedownDamage);
  }
  return h >>> 0;
}
